
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { adminApi } from "@/services/ApiService";
import { StandardProductImage } from "@/api/api";
import ImageEdit from "@/components/controls/image-edit.vue";
import { StandardProductRecord } from "./StandardProductRecord";
import { getStoreThumbnail, getValidatedThumbnail } from "./thumbnails";
import ImagePreview from "./image-preview.vue";

@Component({ components: { ImageEdit, ImagePreview } })
export default class StandardImageEdit extends Vue {
  @Prop()
  record!: StandardProductRecord;

  loading: boolean = false;
  thumbnail: string | null = null;

  modalOpen: boolean = false;
  imagesLoading: boolean = false;
  images: StandardProductImage[] = [];
  thumbnails: { [imageId: number]: string } = {};

  get moreImages() {
    return (this.record.imageCount ?? 0) - 1;
  }

  // What the row currently shows: a store image id, "saved" for the
  // validated image, or null for no image
  get displaySource(): number | "saved" | null {
    if (this.record.pickedImageId !== undefined) return this.record.pickedImageId;
    if (this.record.saved) return this.record.savedHasImage ? "saved" : null;
    return this.record.suggestedImageId ?? null;
  }

  get previewImageId(): number | undefined {
    let source = this.displaySource;
    return typeof source === "number" ? source : undefined;
  }

  get previewPlu(): string | undefined {
    return this.displaySource === "saved" ? this.record.plu : undefined;
  }

  // Image id to highlight in the picker (undefined when the saved image is shown)
  get pickedKey(): number | null | undefined {
    let source = this.displaySource;
    return source === "saved" ? undefined : source;
  }

  @Watch("displaySource", { immediate: true })
  async loadThumbnail() {
    let source = this.displaySource;
    if (source === null) {
      this.thumbnail = null;
      return;
    }
    this.loading = true;
    try {
      let result =
        source === "saved"
          ? await getValidatedThumbnail(this.record.plu ?? "")
          : await getStoreThumbnail(source);
      if (source === this.displaySource) this.thumbnail = result;
    } catch {
      this.thumbnail = null;
    } finally {
      this.loading = false;
    }
  }

  async openModal() {
    if (this.modalOpen) return;
    this.modalOpen = true;
    this.imagesLoading = true;
    try {
      let result = await adminApi.getStandardProductImages(this.record.plu ?? "");
      this.images = result.data.images ?? [];
    } finally {
      this.imagesLoading = false;
    }
    this.images.forEach(async (image) => {
      if (image.imageId === undefined || this.thumbnails[image.imageId]) return;
      let thumbnail = await getStoreThumbnail(image.imageId);
      this.$set(this.thumbnails, image.imageId, thumbnail);
    });
  }

  choose(imageId: number | null) {
    this.modalOpen = false;
    this.$emit("valueChanged", imageId);
  }

  modalCancel() {
    this.modalOpen = false;
  }
}
